import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, Teleport as _Teleport, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "font-bold text-start text-xl" }
const _hoisted_2 = { class: "text-start text-sm text-gray-500 mt-0.5" }
const _hoisted_3 = {
  key: 0,
  class: "flex justify-center items-center py-10 h-64"
}
const _hoisted_4 = { class: "flex flex-wrap gap-2 justify-end items-center" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "As alterações feitas no formulário não serão salvas.", -1)

import { toRefs, onMounted, ref, computed, watch, nextTick, provide } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Network from '@/Network'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import { CreateHealthUnit, PhoneType } from '@/Network/Types/Requests/HealthUnitRequests'
import type { HealthUnitPinMeta } from '@/Network/Types/Requests/PinSuggestionRequests'
import {
  SimpleHealthUnitResponse,
  HealthUnitResponse
} from '@/Network/Types/Responses/HealthUnitResponses'
import { OperationalGroup } from '@/Network/Types/Responses/ScheduleUnitResponses'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { schemaValidateAllFields } from '@/utils/yup'
import { VForm } from 'vuetify/lib/components/index.mjs'
import { SelectOption } from '@/types'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { HealthUnitSchema } from '../schemas'
import { MODAL_ACTION_HEIGHT } from '../constants'
import { useHealthUnitForm, HealthUnitFormKey } from '../composables/useHealthUnitForm'
import { useUnitGeolocation } from '../composables/useUnitGeolocation'
import { useCepLookup } from '../composables/useCepLookup'
import { phoneDigits } from '../helpers/phone'
import { cnpjDigits } from '../helpers/cnpj'
import { postalCodeDigits } from '../helpers/postalCode'
import UnitGeneralFields from '../components/form/UnitGeneralFields.vue'
import UnitAddressFields from '../components/form/UnitAddressFields.vue'
import UnitPinModal from './UnitPinModal/UnitPinModal.vue'
import type { PinConfirmPayload } from './UnitPinModal/pinModalTypes'
import ConfirmActionModal from '../components/ConfirmActionModal.vue'

const ERR_EMPRESA_OBRIGATORIO = 'O campo Empresa é obrigatório'
const ERR_CAMPOS_INVALIDOS = 'Existem campos inválidos'
/**
 * Falha ao SALVAR é erro (a unidade não foi gravada) — diferente da falha na
 * busca do CEP, que é só um aviso e deixa o formulário seguir no manual.
 */
const ERR_SAVE_CREATE = 'Não foi possível cadastrar a unidade.'
const ERR_SAVE_UPDATE = 'Não foi possível salvar as alterações da unidade.'


export default /*#__PURE__*/_defineComponent({
  __name: 'UnitFormModal',
  props: {
    modalStateProp: { type: Boolean },
    type: {},
    healthUnit: {}
  },
  emits: ["close", "created"],
  setup(__props: any, { emit }) {

const props = __props;





const { modalStateProp } = toRefs(props)
const toast = useToast()
const { notifyError } = useErrorToast()
const permissionsStore = usePermissionsStore()

// Estado do formulário: instância própria desta montagem (sem singleton)
const formApi = useHealthUnitForm()
provide(HealthUnitFormKey, formApi)
const { form } = formApi

const geo = useUnitGeolocation()
const cep = useCepLookup()

const listCompanies = ref<SelectOption[]>([])
const companySelected = ref<number>()
const allOperationalGroups = ref<OperationalGroup[]>([])
const fieldOptionsService = new FieldOptionsService()
const operationalGroupSelection = ref<OperationalGroup | null>(null)

// Evita que o watch de postalCode dispare o lookup de CEP quando o campo é
// preenchido programaticamente (populate na edição): CEP geral de cidade
// (ex.: 95560000) volta do ViaCEP com logradouro/bairro vazios e apagaria o
// endereço já cadastrado da unidade.
const suppressCepLookup = ref(false)

const isLocationModalVisible = ref(false)
const isDiscardConfirmOpen = ref(false)
/**
 * Origem/justificativas do pino confirmadas no modal — seguem junto do submit
 * para a auditoria de geolocalização do backend.
 */
const pendingPinMeta = ref<HealthUnitPinMeta | null>(null)

/** Endereço textual do cadastro, o plano B do passo 1 quando o Places falha. */
const registeredAddress = computed(() => {
  const { street, number, neighborhood, city, state, postalCode } = form.values
  return [street, number, neighborhood, city, state, postalCode].filter(Boolean).join(', ')
})

/**
 * Retrato do formulário como ele ficou ao abrir — vazio no cadastro, preenchido na edição.
 * Comparar contra ele é o que diz se há algo a perder ao fechar.
 */
const pristineSnapshot = ref<string | null>(null)
const isLoading = ref(false)
/**
 * Separada do `isLoading` do submit: os dois botões vivem em partes distintas do
 * formulário, e girar o spinner do salvar durante o geocode confundia.
 */
const isVerifyingLocation = ref(false)
const isFetchingData = ref(false)

const formRef = ref<typeof VForm | null>(null)

const disabledInputs = computed<boolean>(
  () => props.type === 'update' && !props.healthUnit?.isAdmin
)

const getTextTooltip = () => {
  return disabledInputs.value
    ? 'Acesso de visualização na unidade'
    : 'Verifique a localização em "Coordenadas" antes de salvar'
}

const canEditHealthUnitGeneral = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.HealthUnitEditing,
    resourceType: AuthorizationResourcesAndSubjects.HealthUnit
  })
})

const titleText = computed(() => {
  if (props.type === 'update') {
    return form.values.name || 'Editar Unidade'
  }
  return 'Cadastrar Unidade'
})

const headerSubtitle = computed(() => {
  return props.type === 'create'
    ? 'Preencha os dados da unidade. Campos com * são obrigatórios.'
    : 'Altere os dados da unidade. Campos com * são obrigatórios.'
})

const buttonSubmitText = computed(() =>
  props.type === 'create' ? 'Cadastrar Unidade' : 'Editar Unidade'
)

watch(operationalGroupSelection, (newSelection) => {
  form.values.operationalGroup = newSelection?.name ?? ''
})

watch(
  () => props.modalStateProp,
  (isOpen) => {
    if (isOpen) {
      initializeModal()
    }
  }
)

watch(
  () => form.values.postalCode,
  (newCep) => {
    if (suppressCepLookup.value) return
    if (newCep) {
      applyCepLookup(newCep)
    }
  }
)

onMounted(() => {
  if (props.modalStateProp) {
    initializeModal()
  }
})

const initializeModal = async () => {
  isFetchingData.value = true
  try {
    try {
      allOperationalGroups.value = await fieldOptionsService.getOperationalGroups()
    } catch (e) {
      console.error('Failed to fetch operational groups', e)
      allOperationalGroups.value = []
      // Campo obrigatório: sem a lista o usuário não consegue salvar e precisa saber por quê.
      notifyError(e, 'Não foi possível carregar os grupos operacionais.')
    }

    const isUpdated = props.type === 'update' && props.healthUnit
    if (isUpdated) {
      // No update, a única empresa selecionável é a da própria unidade
      if (props.healthUnit?.company) {
        listCompanies.value = [
          { title: props.healthUnit.company.name, value: props.healthUnit.company.id }
        ]
      } else {
        listCompanies.value = []
      }

      if (props.healthUnit) {
        await populateForm(props.healthUnit)
      }
    } else {
      try {
        listCompanies.value = await Network.company.listAdminAccess()
      } catch (e) {
        console.error('Failed to fetch companies', e)
        notifyError(e, 'Não foi possível carregar a lista de empresas.')
      }
      clear()
    }
  } catch (error) {
    console.error('Critical error in initializeModal:', error)
    notifyError(error, 'Não foi possível abrir o formulário da unidade.')
  } finally {
    isFetchingData.value = false
    // No `finally` de propósito: se a captura ficasse depois de um `await`, qualquer falha no
    // caminho deixaria o formulário sem retrato — e sem retrato não há aviso de descarte.
    pristineSnapshot.value = snapshotForm()
    setTimeout(() => {
      if (formRef.value) {
        formRef.value.resetValidation()
      }
    }, 100)
  }
  await nextTick()
  if (formRef.value) {
    formRef.value.resetValidation()
  }
}

/** Inclui o que vive fora de `form.values` mas o usuário também preenche. */
function snapshotForm(): string {
  // `values.operationalGroup` é espelho do select, preenchido por um watcher no flush
  // seguinte ao populate — compará-lo aqui marcaria a edição como suja assim que abrisse.
  const values = { ...form.values, operationalGroup: undefined }

  return JSON.stringify({
    values,
    companySelected: companySelected.value,
    operationalGroup: operationalGroupSelection.value?.name ?? null,
    latitude: geo.latitude.value,
    longitude: geo.longitude.value,
    reachRadius: geo.reachRadius.value
  })
}

const isDirty = computed(
  () => pristineSnapshot.value !== null && snapshotForm() !== pristineSnapshot.value
)

const applyCepLookup = async (newCep: string) => {
  const address = await cep.lookup(newCep)
  if (!address) return

  // CEP geral de cidade volta do ViaCEP com logradouro/bairro vazios —
  // nesses casos preserva o valor já preenchido no form.
  if (address.street) {
    form.values.street = address.street
    form.errors.street = ''
  }
  if (address.neighborhood) {
    form.values.neighborhood = address.neighborhood
    form.errors.neighborhood = ''
  }
  if (address.city) {
    form.values.city = address.city
    form.errors.city = ''
  }
  if (address.state) {
    form.values.state = address.state
    form.errors.state = ''
  }
  if (address.complement) {
    form.values.complement = address.complement
  }

  geo.invalidateConfirmation()
}

const populateForm = async (healthUnit: SimpleHealthUnitResponse) => {
  suppressCepLookup.value = true
  try {
    companySelected.value = healthUnit.company?.id
    formApi.populateFromUnit(healthUnit)

    if (healthUnit.operationalGroup && allOperationalGroups.value.length > 0) {
      const groupObject = allOperationalGroups.value.find(
        (g) => g.name === healthUnit.operationalGroup
      )
      operationalGroupSelection.value = groupObject || null
    } else {
      operationalGroupSelection.value = null
    }

    geo.seedFromUnit(healthUnit)
  } catch (e) {
    console.error('Error populating form', e)
    // Sem chamada ao backend aqui: só falha inesperada ao copiar a unidade para o form.
    // Sem o toast o modal abriria com campos faltando e nenhum aviso.
    notifyError(e, 'Não foi possível carregar os dados da unidade.')
  } finally {
    // O watcher de postalCode disparado pelo populate roda no próximo tick;
    // só liberar o lookup depois dele.
    await nextTick()
    suppressCepLookup.value = false
  }
}

async function openLocationVerification() {
  try {
    isVerifyingLocation.value = true
    if (!companySelected.value) {
      toast.error(ERR_EMPRESA_OBRIGATORIO)
      return
    }

    const isValid = await schemaValidateAllFields(form, HealthUnitSchema, formRef)
    if (!isValid) {
      toast.error(ERR_CAMPOS_INVALIDOS)
      return
    }

    // Sem geocode antes de abrir: o passo 1 do modal é quem encontra o prédio
    // (Places, endereço do cadastro ou link) — e na edição o pino atual já vem
    // do `seedFromUnit`.
    // O tipo já escolhido no formulário entra no modal junto (e traz o preset de
    // raio): perguntar de novo no passo 1 seria burocracia.
    if (form.values.type) geo.setUnitType(form.values.type)
    geo.setGoogleMapsLink('')
    isLocationModalVisible.value = true
  } catch (e) {
    console.error(e)
    // As validações acima já avisam e saem pelo `return`; aqui só chega falha inesperada —
    // sem o toast o clique em "verificar localização" não teria efeito visível.
    notifyError(e, 'Não foi possível abrir a verificação de localização.')
  } finally {
    isVerifyingLocation.value = false
  }
}

function closeLocationModal() {
  isLocationModalVisible.value = false
  geo.setGoogleMapsLink('')
  // Sair sem confirmar deixa no `geo` um ponto/raio que ninguém checou no satélite:
  // manter a verificação anterior mandaria ao backend um `pinMeta` velho jurando
  // `confirmedOnBuilding: true` sobre outras coordenadas. Quem confirma fecha pelo
  // `onPinConfirmed`, que não passa por aqui.
  geo.invalidateConfirmation()
  pendingPinMeta.value = null
}

function onPinConfirmed(payload: PinConfirmPayload) {
  pendingPinMeta.value = {
    origin: payload.origin,
    reason: payload.reason ?? undefined,
    reasonDetail: payload.reasonDetail || undefined,
    radiusJustification: payload.radiusJustification || undefined,
    confirmedOnBuilding: true,
    suggestionId: payload.suggestionId ?? undefined
  }
  // O tipo escolhido no passo 1 é o mesmo campo obrigatório do formulário.
  form.values.type = payload.type
  geo.confirmLocation()
  isLocationModalVisible.value = false
}

// eslint-disable-next-line complexity
const submit = async () => {
  try {
    if (!companySelected.value) {
      toast.error(ERR_EMPRESA_OBRIGATORIO)
      return
    }

    isLoading.value = true
    form.values.reachRadius = geo.reachRadius.value
    const isValid = await schemaValidateAllFields(form, HealthUnitSchema, formRef)
    if (!isValid) {
      // O raio de alcance mora no modal de localização, fechado neste ponto: sem
      // mensagem própria o erro dele viraria um "campos inválidos" sem campo à vista.
      toast.error(form.errors.reachRadius || ERR_CAMPOS_INVALIDOS)
      return
    }

    if (!geo.locationConfirmed.value) {
      toast.error('Por favor, verifique a localização antes de prosseguir.')
      return
    }

    const { city, street, neighborhood, number, postalCode, complement, state } = form.values
    const mainPhoneDigits = phoneDigits(form.values.mainPhone)
    const employees = Number(form.values.quantityColaborators)

    const payload: CreateHealthUnit = {
      cnpj: cnpjDigits(form.values.cnpj) || undefined,
      email: form.values.email || undefined,
      generalManager: form.values.generalManager || undefined,
      mainAddress: {
        city,
        street,
        complement,
        neighborhood,
        number,
        postalCode: postalCodeDigits(postalCode),
        state,
        latitude: Number(geo.latitude.value),
        longitude: Number(geo.longitude.value)
      },
      mainPhone: mainPhoneDigits
        ? { phoneNumber: mainPhoneDigits, phoneType: PhoneType.COMMERCIAL }
        : undefined,
      name: form.values.name,
      type: form.values.type,
      profile: form.values.profile,
      quantityColaborators: Number.isFinite(employees) ? employees : undefined,
      technicalManager: form.values.technicalManager,
      linkWaze: form.values.linkWaze,
      reachRadius: form.values.reachRadius,
      operationalGroup: form.values.operationalGroup || null,
      pinMeta: pendingPinMeta.value ?? undefined
    }

    if (props.type === 'create') {
      await handleSubmitCreate(companySelected.value, payload)
    } else {
      await handleSubmitUpdate(payload)
    }

    emit('close')
  } catch (error) {
    notifyError(error, props.type === 'create' ? ERR_SAVE_CREATE : ERR_SAVE_UPDATE)
  } finally {
    isLoading.value = false
  }
}

const handleSubmitUpdate = async (payload: CreateHealthUnit) => {
  if (!props.healthUnit) return
  const healthUnitId = props.healthUnit.id
  await Network.healthUnit.update(healthUnitId, payload)
  toast.success('Unidade alterada com sucesso')
}

const handleSubmitCreate = async (companyId: number, payload: CreateHealthUnit) => {
  const newHealthUnit = await Network.healthUnit.create(companyId, payload)
  emit('created', newHealthUnit)
  toast.success('Unidade cadastrada com sucesso')
}

const close = () => {
  // O Esc e o clique no overlay chegam aos dois modais: enquanto a confirmação está aberta,
  // o formulário atrás dela ignora pedidos de fechamento.
  if (isDiscardConfirmOpen.value) return

  if (isDirty.value) {
    isDiscardConfirmOpen.value = true
    return
  }

  clear()
  emit('close')
}

function discardAndClose() {
  isDiscardConfirmOpen.value = false
  clear()
  emit('close')
}

const clear = () => {
  formApi.resetForm()
  companySelected.value = undefined
  operationalGroupSelection.value = null
  pendingPinMeta.value = null
  cep.clearWarning()
  // Divergência intencional do modal antigo: geo.reset() também zera
  // locationConfirmed/foundLocation — o original deixava a verificação
  // "stale" vazar para a próxima abertura (bug do singleton).
  geo.reset()
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalVue, {
      onClose: close,
      modalActive: _unref(modalStateProp),
      "has-header-border": "",
      "has-footer-border": ""
    }, {
      header: _withCtx(() => [
        _createElementVNode("p", _hoisted_1, _toDisplayString(titleText.value), 1),
        _createElementVNode("p", _hoisted_2, _toDisplayString(headerSubtitle.value), 1)
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(OutlinedButton, {
            onClick: close,
            text: "Cancelar",
            width: "140px",
            height: _unref(MODAL_ACTION_HEIGHT),
            "data-testid": "unit-form-cancel-button"
          }, null, 8, ["height"]),
          _createVNode(_component_v_tooltip, {
            location: "top",
            disabled: _unref(geo).locationConfirmed.value || disabledInputs.value,
            text: `${getTextTooltip()}`
          }, {
            activator: _withCtx(({ props: tooltipProps }) => [
              _createElementVNode("div", _normalizeProps(_guardReactiveProps(tooltipProps)), [
                _createVNode(FilledButton, {
                  onClick: submit,
                  text: buttonSubmitText.value,
                  loading: isLoading.value,
                  disabled: !_unref(geo).locationConfirmed.value || isLoading.value || !canEditHealthUnitGeneral.value,
                  width: "200px",
                  height: _unref(MODAL_ACTION_HEIGHT),
                  "data-testid": "unit-form-submit-button"
                }, null, 8, ["text", "loading", "disabled", "height"])
              ], 16)
            ]),
            _: 1
          }, 8, ["disabled", "text"])
        ])
      ]),
      default: _withCtx(() => [
        (isFetchingData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_component_v_progress_circular, {
                indeterminate: "",
                color: "primary",
                size: "50"
              })
            ]))
          : (_openBlock(), _createBlock(_unref(VForm), {
              key: 1,
              ref_key: "formRef",
              ref: formRef,
              class: "pt-2",
              "data-testid": "unit-form"
            }, {
              default: _withCtx(() => [
                _createVNode(UnitGeneralFields, {
                  disabled: disabledInputs.value,
                  companies: listCompanies.value,
                  "operational-groups": allOperationalGroups.value,
                  "company-selected": companySelected.value,
                  "operational-group": operationalGroupSelection.value,
                  "onUpdate:companySelected": _cache[0] || (_cache[0] = ($event: any) => (companySelected.value = $event)),
                  "onUpdate:operationalGroup": _cache[1] || (_cache[1] = ($event: any) => (operationalGroupSelection.value = $event))
                }, null, 8, ["disabled", "companies", "operational-groups", "company-selected", "operational-group"]),
                _createVNode(UnitAddressFields, {
                  class: "mt-6",
                  disabled: disabledInputs.value,
                  "is-loading-cep": _unref(cep).isLoadingCep.value,
                  "cep-warning": _unref(cep).lookupWarning.value,
                  latitude: _unref(geo).latitude.value,
                  longitude: _unref(geo).longitude.value,
                  "location-confirmed": _unref(geo).locationConfirmed.value,
                  "is-verifying-location": isVerifyingLocation.value,
                  onAddressChanged: _cache[2] || (_cache[2] = ($event: any) => (_unref(geo).invalidateConfirmation())),
                  onVerifyLocation: openLocationVerification
                }, null, 8, ["disabled", "is-loading-cep", "cep-warning", "latitude", "longitude", "location-confirmed", "is-verifying-location"])
              ]),
              _: 1
            }, 512))
      ]),
      _: 1
    }, 8, ["modalActive"]),
    _createVNode(ConfirmActionModal, {
      "modal-active": isDiscardConfirmOpen.value,
      title: "Descartar alterações?",
      "confirm-text": "Descartar",
      "confirm-class": "bg-negative",
      onClose: _cache[3] || (_cache[3] = ($event: any) => (isDiscardConfirmOpen.value = false)),
      onConfirm: discardAndClose
    }, {
      default: _withCtx(() => [
        _hoisted_5
      ]),
      _: 1
    }, 8, ["modal-active"]),
    (_openBlock(), _createBlock(_Teleport, { to: "#app" }, [
      _createVNode(UnitPinModal, {
        mode: "form",
        "modal-active": isLocationModalVisible.value,
        geo: _unref(geo),
        "unit-name": _unref(form).values.name,
        "registered-address": registeredAddress.value,
        "health-unit-id": props.healthUnit?.id ?? null,
        onClose: closeLocationModal,
        onConfirm: onPinConfirmed
      }, null, 8, ["modal-active", "geo", "unit-name", "registered-address", "health-unit-id"])
    ]))
  ], 64))
}
}

})